// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  ActionIcon,
  Box,
  Divider,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useMedplum } from '@medplum/react';
import {
  IconCalendarMonth,
  IconCheck,
  IconChevronDown,
  IconClipboardCheck,
  IconLayoutDashboard,
  IconLogout,
  IconMessageCircle,
  IconPill,
  IconSelector,
  IconSettings,
  IconSparkles,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import type { Practitioner } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useSidebar } from '../../contexts/SidebarContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  getPractitionerInitials,
  getPractitionerPhotoUrl,
  getOrganizationLogoUrl,
} from '../../utils/imageUtils';
import { clearSessionStorage } from '../../utils/secureLogout';
import { AuthenticatedAvatar } from '../AuthenticatedAvatar';
import { hasDoseSpotIdentifier } from '../utils';
import classes from './Sidebar.module.css';

interface NavItemProps {
  icon: JSX.Element;
  label: string;
  href: string;
}

interface NavSection {
  title: string;
  items: NavItemProps[];
}

export function Sidebar(): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const membership = medplum.getProjectMembership();
  const hasDoseSpot = hasDoseSpotIdentifier(membership);
  const profile = medplum.getProfile();
  const {
    organizations,
    currentOrganization,
    requestOrganizationSwitch,
  } = useOrganization();

  // Use sidebar context for collapse/expand behavior
  const { mode, isExpanded, handleMouseEnter, handleMouseLeave } = useSidebar();
  // In hover mode, sidebar collapses when mouse leaves and expands when mouse enters
  const isCollapsed = mode === 'hover' && !isExpanded;

  const isActive = (href: string): boolean => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const handleNavClick = (href: string): void => {
    navigate(href);
  };

  const handleLogout = (): void => {
    // Clear all session and project-specific storage BEFORE signing out
    // This ensures no tokens or PHI remain after logout
    clearSessionStorage();
    // Sign out from Medplum (clears SDK tokens)
    medplum.signOut();
    // Redirect to signin page
    navigate('/signin');
  };

  // Build navigation sections - flat structure with section headers
  const navSections: NavSection[] = [
    {
      title: t('nav.sections.main', 'MAIN'),
      items: [
        { icon: <IconLayoutDashboard size={18} />, label: t('nav.dashboard'), href: '/practice' },
        { icon: <IconUsers size={18} />, label: t('nav.patients'), href: '/patients' },
        { icon: <IconCalendarMonth size={18} />, label: t('nav.calendar', 'Calendar'), href: '/appointments' },
      ],
    },
    {
      title: t('nav.sections.tools', 'TOOLS'),
      items: [
        { icon: <IconSparkles size={18} />, label: t('nav.noukGpt'), href: '/spaces' },
        { icon: <IconMessageCircle size={18} />, label: t('nav.messages', 'Messages'), href: '/Message' },
        { icon: <IconClipboardCheck size={18} />, label: t('nav.tasks'), href: '/Task' },
        ...(hasDoseSpot ? [{ icon: <IconPill size={18} />, label: t('nav.doseSpot'), href: '/integrations/dosespot' }] : []),
      ],
    },
    {
      title: t('nav.sections.settings', 'SETTINGS'),
      items: [
        { icon: <IconUsersGroup size={18} />, label: t('nav.staff'), href: '/staff' },
        { icon: <IconSettings size={18} />, label: t('nav.settings'), href: '/settings' },
      ],
    },
  ];

  const renderNavItem = (item: NavItemProps): JSX.Element => {
    const active = isActive(item.href);

    // In collapsed mode, render only icons with tooltips
    if (isCollapsed) {
      return (
        <Tooltip key={item.label} label={item.label} position="right" withArrow>
          <UnstyledButton
            className={classes.navLinkCollapsed}
            data-active={active || undefined}
            onClick={() => handleNavClick(item.href)}
          >
            {item.icon}
          </UnstyledButton>
        </Tooltip>
      );
    }

    return (
      <NavLink
        key={item.label}
        label={item.label}
        leftSection={item.icon}
        active={active}
        onClick={() => handleNavClick(item.href)}
        className={classes.navLink}
      />
    );
  };

  const renderSection = (section: NavSection, index: number): JSX.Element => {
    return (
      <Box key={section.title}>
        {!isCollapsed && (
          <Text className={classes.sectionHeader}>
            {section.title}
          </Text>
        )}
        {isCollapsed && index > 0 && <Divider my="xs" />}
        {section.items.map((item) => renderNavItem(item))}
      </Box>
    );
  };

  const sidebarClassName = `${classes.sidebar} ${isCollapsed ? classes.sidebarCollapsed : ''}`;

  return (
    <Box
      className={classes.sidebarWrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box className={sidebarClassName}>
        {/* Clinic/Practice Switcher */}
        <Box className={classes.clinicSwitcher}>
          {isCollapsed ? (
            <Tooltip label={currentOrganization?.name || t('nav.selectPractice')} position="right" withArrow>
              <Menu position="right-start" width={260} shadow="md">
                <Menu.Target>
                  <UnstyledButton className={classes.clinicSwitcherCollapsed}>
                    <AuthenticatedAvatar
                      size={32}
                      radius="md"
                      color="blue"
                      src={getOrganizationLogoUrl(currentOrganization)}
                    >
                      {currentOrganization?.name?.charAt(0) || 'P'}
                    </AuthenticatedAvatar>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>{t('nav.switchPractice', 'Switch Practice')}</Menu.Label>
                  {organizations.map((org) => (
                    <Menu.Item
                      key={org.id}
                      onClick={() => requestOrganizationSwitch(org)}
                      leftSection={
                        <AuthenticatedAvatar size={24} radius="md" color="blue" src={getOrganizationLogoUrl(org)}>
                          {org.name?.charAt(0) || 'O'}
                        </AuthenticatedAvatar>
                      }
                      rightSection={org.id === currentOrganization?.id ? <IconCheck size={14} color="green" /> : null}
                    >
                      <Text size="sm">{org.name}</Text>
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            </Tooltip>
          ) : (
            <Menu position="bottom-start" width={260} shadow="md">
              <Menu.Target>
                <UnstyledButton className={classes.clinicSwitcherButton}>
                  <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
                    <AuthenticatedAvatar
                      size={32}
                      radius="md"
                      color="blue"
                      src={getOrganizationLogoUrl(currentOrganization)}
                    >
                      {currentOrganization?.name?.charAt(0) || 'P'}
                    </AuthenticatedAvatar>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={500} lineClamp={1}>
                        {currentOrganization?.name || t('nav.selectPractice')}
                      </Text>
                      {currentOrganization?.address?.[0]?.city && (
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {currentOrganization.address[0].city}
                        </Text>
                      )}
                    </Box>
                  </Group>
                  <IconSelector size={16} className={classes.clinicChevron} />
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{t('nav.switchPractice', 'Switch Practice')}</Menu.Label>
                {organizations.map((org) => (
                  <Menu.Item
                    key={org.id}
                    onClick={() => requestOrganizationSwitch(org)}
                    leftSection={
                      <AuthenticatedAvatar size={24} radius="md" color="blue" src={getOrganizationLogoUrl(org)}>
                        {org.name?.charAt(0) || 'O'}
                      </AuthenticatedAvatar>
                    }
                    rightSection={org.id === currentOrganization?.id ? <IconCheck size={14} color="green" /> : null}
                  >
                    <Text size="sm">{org.name}</Text>
                    {org.address?.[0]?.city && (
                      <Text size="xs" c="dimmed">
                        {org.address[0].city}
                      </Text>
                    )}
                  </Menu.Item>
                ))}
                {organizations.length === 0 && (
                  <Menu.Item disabled>
                    <Text size="sm" c="dimmed">
                      {t('nav.noPracticesAvailable', 'No practices available')}
                    </Text>
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Box>

        <Divider />

        {/* Main Navigation with Sections */}
        <ScrollArea className={classes.navSection} type="scroll">
          <Box py="sm">
            {navSections.map((section, index) => renderSection(section, index))}
          </Box>
        </ScrollArea>

        {/* Footer with user info */}
        <Box className={classes.footer}>
          <Divider mb="sm" />
          <Group
            className={classes.footerGroup}
            justify={isCollapsed ? 'center' : 'space-between'}
            px={isCollapsed ? 'xs' : 'md'}
            py="xs"
          >
            <Group gap="sm">
              <AuthenticatedAvatar
                size={isCollapsed ? 32 : 36}
                radius="xl"
                color="blue"
                src={getPractitionerPhotoUrl(profile as Practitioner)}
              >
                {getPractitionerInitials(profile as Practitioner)}
              </AuthenticatedAvatar>
              {!isCollapsed && (
                <Box className={classes.userText}>
                  <Text size="sm" fw={500} lineClamp={1}>
                    {profile?.name?.[0]?.given?.[0] || 'User'}
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {profile?.name?.[0]?.family || ''}
                  </Text>
                </Box>
              )}
            </Group>
            {isCollapsed ? (
              <Tooltip label={t('nav.logout')} position="right" withArrow>
                <ActionIcon variant="subtle" color="red" onClick={handleLogout}>
                  <IconLogout size={18} />
                </ActionIcon>
              </Tooltip>
            ) : (
              <Tooltip label={t('nav.logout')} position="top" withArrow>
                <ActionIcon variant="subtle" color="red" onClick={handleLogout}>
                  <IconLogout size={18} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Box>
      </Box>
    </Box>
  );
}
